/**
 * i18next configuration — Phase 8D Wave 1.
 *
 * Translation strategy:
 *   1. Browser language detected (navigator.language → localStorage)
 *   2. Falls back to 'en' for any missing key (no zh-CN/zh-TW regional fallback yet)
 *   3. Domain-correct translations come from v2_server/api/services/llm_translation.py
 *      — NOT raw machine translation; KB-retrieved industry terms drive the LLM.
 *   4. ESLint rule (added in 8D step 8) flags hardcoded English in .tsx files.
 *
 * Namespaces follow the master roadmap:
 *   - common         (buttons, navigation, generic labels)
 *   - industries     (per-industry asset types, KPIs, terminology)
 *   - widgets        (widget titles, axis labels, legend entries)
 *   - errors         (error messages, validation)
 *   - regulatory     (compliance text — NEVER auto-translated; SME-reviewed)
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Eager-loaded English (master). Other languages loaded lazily on first switch.
import enCommon from './locales/en/common.json'
import enIndustries from './locales/en/industries.json'
import enWidgets from './locales/en/widgets.json'
import enErrors from './locales/en/errors.json'
import enRegulatory from './locales/en/regulatory.json'

export const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'fr', 'zh', 'ja'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  zh: '中文',
  ja: '日本語',
}

export const NAMESPACES = ['common', 'industries', 'widgets', 'errors', 'regulatory'] as const

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',

    // Bundle English upfront; other locales load on switch via dynamic import
    resources: {
      en: {
        common: enCommon,
        industries: enIndustries,
        widgets: enWidgets,
        errors: enErrors,
        regulatory: enRegulatory,
      },
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'ryedore.language',
    },

    react: {
      useSuspense: false, // we lazy-load locales manually to avoid Suspense boundaries
    },
  })

/**
 * Lazy-load a non-English locale on first switch. Called by the language
 * selector. No-op if the language is already loaded.
 */
export async function loadLanguage(lang: SupportedLanguage): Promise<void> {
  if (lang === 'en' || i18n.hasResourceBundle(lang, 'common')) return
  try {
    const [common, industries, widgets, errors, regulatory] = await Promise.all([
      import(`./locales/${lang}/common.json`),
      import(`./locales/${lang}/industries.json`),
      import(`./locales/${lang}/widgets.json`),
      import(`./locales/${lang}/errors.json`),
      import(`./locales/${lang}/regulatory.json`),
    ])
    i18n.addResourceBundle(lang, 'common', common.default || common)
    i18n.addResourceBundle(lang, 'industries', industries.default || industries)
    i18n.addResourceBundle(lang, 'widgets', widgets.default || widgets)
    i18n.addResourceBundle(lang, 'errors', errors.default || errors)
    i18n.addResourceBundle(lang, 'regulatory', regulatory.default || regulatory)
  } catch (e) {
    console.warn(`Failed to load locale ${lang}:`, e)
  }
}

/**
 * Switch the active language. Lazy-loads the locale bundle first if needed.
 * The <html lang> attribute updates as a side effect for a11y.
 */
export async function setLanguage(lang: SupportedLanguage): Promise<void> {
  await loadLanguage(lang)
  await i18n.changeLanguage(lang)
  document.documentElement.lang = lang
  localStorage.setItem('ryedore.language', lang)
}

export default i18n
